<template>
  <div class="signing-record-page">
    <div v-if="state.loading" class="signing-record-loading">
      <van-loading type="spinner" size="28px" />
    </div>

    <template v-else>
      <div
        v-for="(item, index) in state.list"
        :key="item.id ?? index"
        class="signing-record-card"
      >
        <!-- 顶部区域 -->
        <div class="card-top">
          <div class="card-row card-row--head">
            <div class="card-head-left">
              <span class="card-title">{{ item.businessName || "--" }}</span>
              <span class="card-price">¥ {{ formatMoney(item.signMoney) }}</span>
            </div>
            <span class="card-status">{{
              SigningStatusText[item.signStatus] ?? "--"
            }}</span>
          </div>
          <div class="card-row card-row--dates">
            <span class="card-dates"
              >{{ getDay(item.checkInDate) }} ~ {{ getDay(item.expireDate) }}</span
            >
            <span class="card-remain" v-if="item.limitNumber"
              >次数:{{ item.limitNumber ?? "--" }}</span
            >
          </div>
        </div>

        <!-- 底部已签到 -->
        <div
          class="card-bottom"
          role="button"
          tabindex="0"
          @click="handleGoToSignin(item)"
        >
          <div class="card-bottom-text">
            <span class="card-bottom-label">已签到</span>
            <span class="card-bottom-days">{{ item.record ?? "--" }}天</span>
          </div>
          <van-icon name="arrow" class="card-arrow" />
        </div>
      </div>

      <div v-if="!state.list.length" class="signing-record-empty">
        <EmptyData />
      </div>
    </template>
  </div>
</template>

<script>
import { defineComponent, onMounted, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import { queryGetSignParentOrStu } from "@/api/user";
import { SigningStatusText } from "@/utils/const";
import EmptyData from "@/components/EmptyData.vue";
import dayjs from "dayjs";

export default defineComponent({
  name: "SigningRecord",

  components: {
    EmptyData,
  },

  setup() {
    const route = useRoute();
    const router = useRouter();

    const state = reactive({
      list: [],
      loading: true,
    });

    onMounted(() => {
      if(route?.query?.token) {
        localStorage.setItem('token', route?.query?.token)
      }
      queryGetSignParentOrStuData();
    });

    const queryGetSignParentOrStuData = async () => {
      state.loading = true;
      try {
        const result = await queryGetSignParentOrStu({});
        if (result.code === "00") {
          state.list = (result.data ?? [])
        } else {
          state.list = [];
        }
      } catch (error) {
        console.log("error:>>>", error);
        state.list = [];
      } finally {
        state.loading = false;
      }
    };

    const handleGoToSignin = (row) => {
      router.push({
        path: "/signIn",
        query: {
          userId: route?.query?.userId,
          businessUserId: row?.businessUserId,
          userName: row?.realName,
          checkInDate: row?.checkInDate,
          expireDate: row?.expireDate,
          performanceId: row?.performanceId,
          source: 'signAContract',
        },
      });
    };

    const getDay = (day) => {
      if (!day) return "--";
      return dayjs(day).format("YYYY-MM-DD");
    };

    const formatMoney = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0";
      return n % 1 === 0 ? String(n) : n.toFixed(2);
    };

    return {
      state,
      SigningStatusText,
      handleGoToSignin,
      getDay,
      formatMoney,
    };
  },
});
</script>

<style lang="scss" scoped>
$bg-page: #f4f7f9;
$accent: #d2a06d;
$text-primary: #333333;
$text-muted: #666666;

.signing-record-page {
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  background: $bg-page;
  padding: 10px 10px 24px;
  overflow-y: auto;
  text-align: left;
}

.signing-record-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40vh;
}

.signing-record-empty {
  padding-top: 32px;
}

.signing-record-card {
  background: #ffffff;
  border-radius: 10px;
  margin-bottom: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.card-top {
  padding: 15px 15px 12px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;

  &--head {
    align-items: flex-start;
    padding-top: 0;
  }

  &--dates {
    padding-top: 6px;
  }
}

.card-head-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 8px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: $text-primary;
  line-height: 1.4;
}

.card-price {
  font-size: 15px;
  font-weight: 500;
  color: $accent;
}

.card-status {
  flex-shrink: 0;
  font-size: 13px;
  color: $text-muted;
  margin-left: 8px;
}

.card-dates {
  font-size: 13px;
  color: $text-muted;
}

.card-remain {
  flex-shrink: 0;
  font-size: 13px;
  color: $text-muted;
}

.card-extra {
  font-size: 13px;
  color: $text-muted;
}

.card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  border-top: 1px solid #e8ecf0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: #f8f9fb;
  }
}

.card-bottom-text {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.card-bottom-label {
  font-size: 15px;
  font-weight: 600;
  color: #000000;
}

.card-bottom-days {
  font-size: 15px;
  font-weight: 500;
  color: $accent;
}

.card-arrow {
  font-size: 16px;
  color: #c0c4cc;
}
</style>
